import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from "formik";
import Button from "@mui/material/Button";
import * as yup from "yup";
import { useNavigate, useParams } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Container from "../../GeneralComponents/Container";
import { Topbar, Sidebar } from '../components';
import setGlobalLoader from '../../../redux/actions/loaderActions'
import { getOrganizations, getStudentById, updateStudent } from '../../../redux/actions/apiActions'
import { STUDENT_STATUS, PROFILES, BLOOD_GROUPS } from '../../../utils/constants';
import { statesAndCities } from '../../../utils/indianStatesCities';
import { CircularProgress } from '@mui/material';
import { format } from 'date-fns';
import { s3Upload } from '../../../utils/uploadService';
import { compressImageWithDimension } from '../../../utils/imageCompressor';

const BLOOD_GROUP_OPTIONS = Object.values(BLOOD_GROUPS).map(bg => ({
    value: bg,
    label: bg,
}));

const validationSchema = yup.object({
    name: yup
        .string()
        .trim()
        .required("Name is required."),
    phoneNumber: yup
        .string()
        .trim()
        .required("Phone Number is required.")
        .min(10, "Phone number short than 10 digits.")
        .max(10, "Phone number long than 10 digits."),
    email: yup
        .string()
        .email("Invalid email address.")
        .nullable(),
    guardianPhoneNumber: yup
        .string()
        .trim()
        .min(10, "Phone number short than 10 digits.")
        .max(10, "Phone number long than 10 digits.")
        .nullable(),
})

const EditStudent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const profile = useSelector(state => state.authReducer.profile);
    const [organizations, setOrganizations] = useState([]);
    const [studentDetails, setStudentDetails] = useState(null);
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });

    // State and City dropdowns
    const [selectedState, setSelectedState] = useState(null);
    const [stateOptions, setStateOptions] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [cityOptions, setCityOptions] = useState([]);

    // Documents state
    const [documents, setDocuments] = useState([]);
    const [newDocName, setNewDocName] = useState('');

    // File upload states
    const [profileImgFile, setProfileImgFile] = useState(null);
    const [profileImgPreview, setProfileImgPreview] = useState(null);
    const [learningLicenseImgFile, setLearningLicenseImgFile] = useState(null);
    const [learningLicenseImgPreview, setLearningLicenseImgPreview] = useState(null);
    const [addressProofImgFile, setAddressProofImgFile] = useState(null);
    const [addressProofImgPreview, setAddressProofImgPreview] = useState(null);

    // File input refs
    const profileImgInputRef = useRef(null);
    const learningLicenseInputRef = useRef(null);
    const addressProofInputRef = useRef(null);
    const documentInputRef = useRef(null);

    // Document file upload
    const [newDocFile, setNewDocFile] = useState(null);
    const [newDocPreview, setNewDocPreview] = useState(null);

    // Image preview dialog
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');

    const [openSidebar, setOpenSidebar] = useState(false);

    const handleSidebarOpen = () => {
        setOpenSidebar(true);
    };

    const handleSidebarClose = () => {
        setOpenSidebar(false);
    };
    const open = isMd ? false : openSidebar;

    // Initialize state options
    useEffect(() => {
        setStateOptions(statesAndCities.map(item => ({ label: item.state, value: item.state })));
    }, []);

    // Update city options when state changes
    useEffect(() => {
        if (selectedState) {
            const stateData = statesAndCities.find(s => s.state.toLowerCase() === selectedState.value.toLowerCase());
            if (stateData) {
                const cities = [...stateData.cities].sort((a, b) => a.localeCompare(b));
                setCityOptions(cities.map(city => ({ label: city, value: city })));
            }
        } else {
            setCityOptions([]);
        }
    }, [selectedState]);

    useEffect(() => {
        if (profile === PROFILES.ADMIN) {
            async function organizationFunc() {
                try {
                    const orgRes = await getOrganizations();
                    const data = orgRes?.data?.data?.docs?.map((d) => ({
                        label: d?.name,
                        value: d?._id,
                    }))
                    setOrganizations(data);
                } catch (error) {
                    console.log("get Organizations error", error);
                    toast.error(error.response.data.msg ?? "some error occured while fetching organization list");
                }
            }
            organizationFunc();
        }
    }, [profile])

    useEffect(() => {
        if (params.id !== null && params.id !== undefined) {
            dispatch(setGlobalLoader(true))
            async function studentFunc() {
                try {
                    const studentRes = await getStudentById(params.id);
                    const data = studentRes?.data?.data;
                    setStudentDetails(data);
                    // Set state and city
                    if (data?.state) {
                        const stateOption = { value: data.state, label: data.state };
                        setSelectedState(stateOption);
                        // Load cities for this state
                        const stateData = statesAndCities.find(s => s.state.toLowerCase() === data.state.toLowerCase());
                        if (stateData) {
                            const cities = [...stateData.cities].sort((a, b) => a.localeCompare(b));
                            setCityOptions(cities.map(city => ({ label: city, value: city })));
                        }
                    }
                    if (data?.city) {
                        setSelectedCity({ value: data.city, label: data.city });
                    }
                    // Set documents
                    if (data?.documents && data.documents.length > 0) {
                        setDocuments(data.documents);
                    }
                    // Set existing image previews
                    if (data?.imageUrl) {
                        setProfileImgPreview(data.imageUrl);
                    }
                    if (data?.learningLicenseImg) {
                        setLearningLicenseImgPreview(data.learningLicenseImg);
                    }
                    if (data?.addressProofImg) {
                        setAddressProofImgPreview(data.addressProofImg);
                    }
                    dispatch(setGlobalLoader(false))
                } catch (error) {
                    dispatch(setGlobalLoader(false))
                    console.log("get student details error", error);
                    toast.error(error.response.data.msg ?? "some error occured while fetching student details");
                }
            }
            studentFunc();
        }
    }, [params.id])

    // Handle image selection
    const handleImageSelect = (event, type) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please select a valid image file (JPEG, PNG, or WebP)');
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Image size should be less than 10MB');
            return;
        }

        const preview = URL.createObjectURL(file);

        switch (type) {
            case 'profile':
                setProfileImgFile(file);
                setProfileImgPreview(preview);
                break;
            case 'learningLicense':
                setLearningLicenseImgFile(file);
                setLearningLicenseImgPreview(preview);
                break;
            case 'addressProof':
                setAddressProofImgFile(file);
                setAddressProofImgPreview(preview);
                break;
            case 'document':
                setNewDocFile(file);
                setNewDocPreview(preview);
                break;
            default:
                break;
        }
    };

    // Handle image removal
    const handleRemoveImage = (type, setFieldValue) => {
        switch (type) {
            case 'profile':
                setProfileImgFile(null);
                setProfileImgPreview(null);
                if (setFieldValue) setFieldValue('imageUrl', '');
                if (profileImgInputRef.current) profileImgInputRef.current.value = '';
                break;
            case 'learningLicense':
                setLearningLicenseImgFile(null);
                setLearningLicenseImgPreview(null);
                if (setFieldValue) setFieldValue('learningLicenseImg', '');
                if (learningLicenseInputRef.current) learningLicenseInputRef.current.value = '';
                break;
            case 'addressProof':
                setAddressProofImgFile(null);
                setAddressProofImgPreview(null);
                if (setFieldValue) setFieldValue('addressProofImg', '');
                if (addressProofInputRef.current) addressProofInputRef.current.value = '';
                break;
            case 'document':
                setNewDocFile(null);
                setNewDocPreview(null);
                if (documentInputRef.current) documentInputRef.current.value = '';
                break;
            default:
                break;
        }
    };

    // Handle preview click
    const handlePreviewClick = (imageUrl) => {
        setPreviewImage(imageUrl);
        setPreviewOpen(true);
    };

    const handleAddDocument = async () => {
        if (!newDocName.trim()) {
            toast.error('Please enter a document name');
            return;
        }
        if (!newDocFile) {
            toast.error('Please select a document image');
            return;
        }

        dispatch(setGlobalLoader(true));
        try {
            const compressedFile = await compressImageWithDimension(newDocFile, 1200, 0.9);
            const uploadResult = await s3Upload(compressedFile, { entity: 'student', entityId: params.id || 'edit' });
            if (uploadResult && uploadResult[0]?.url) {
                setDocuments([...documents, { name: newDocName.trim(), url: uploadResult[0].url }]);
                setNewDocName('');
                setNewDocFile(null);
                setNewDocPreview(null);
                if (documentInputRef.current) documentInputRef.current.value = '';
                toast.success('Document uploaded successfully');
            }
        } catch (error) {
            console.log('Document upload error:', error);
            toast.error('Failed to upload document');
        }
        dispatch(setGlobalLoader(false));
    };

    const handleRemoveDocument = (index) => {
        setDocuments(documents.filter((_, i) => i !== index));
    };

    const handleOnSubmit = async (values) => {
        dispatch(setGlobalLoader(true));

        // Clean up empty string values to null
        const cleanedValues = { ...values };
        Object.keys(cleanedValues).forEach(key => {
            if (cleanedValues[key] === '') {
                cleanedValues[key] = null;
            }
        });

        // Add documents
        cleanedValues.documents = documents;

        try {
            // Upload profile image if new file selected
            if (profileImgFile) {
                const compressedFile = await compressImageWithDimension(profileImgFile, 800, 0.85);
                const uploadResult = await s3Upload(compressedFile, { entity: 'student', entityId: params.id });
                if (uploadResult && uploadResult[0]?.url) {
                    cleanedValues.imageUrl = uploadResult[0].url;
                }
            } else if (profileImgPreview && !profileImgPreview.startsWith('blob:')) {
                // Keep existing URL if no new file and there's an existing URL
                cleanedValues.imageUrl = profileImgPreview;
            }

            // Upload learning license image if new file selected
            if (learningLicenseImgFile) {
                const compressedFile = await compressImageWithDimension(learningLicenseImgFile, 1200, 0.9);
                const uploadResult = await s3Upload(compressedFile, { entity: 'student', entityId: params.id });
                if (uploadResult && uploadResult[0]?.url) {
                    cleanedValues.learningLicenseImg = uploadResult[0].url;
                }
            } else if (learningLicenseImgPreview && !learningLicenseImgPreview.startsWith('blob:')) {
                // Keep existing URL if no new file and there's an existing URL
                cleanedValues.learningLicenseImg = learningLicenseImgPreview;
            }

            // Upload address proof image if new file selected
            if (addressProofImgFile) {
                const compressedFile = await compressImageWithDimension(addressProofImgFile, 1200, 0.9);
                const uploadResult = await s3Upload(compressedFile, { entity: 'student', entityId: params.id });
                if (uploadResult && uploadResult[0]?.url) {
                    cleanedValues.addressProofImg = uploadResult[0].url;
                }
            } else if (addressProofImgPreview && !addressProofImgPreview.startsWith('blob:')) {
                // Keep existing URL if no new file and there's an existing URL
                cleanedValues.addressProofImg = addressProofImgPreview;
            }

            await updateStudent(params.id, cleanedValues);
            toast.success("Student updated successfully");
            navigate("/students")
            dispatch(setGlobalLoader(false))
        } catch (error) {
            dispatch(setGlobalLoader(false))
            console.log("update student error - admin", error);
            toast.error(error.response.data.msg ?? "Some error occured.");
        }
    }

    const statusOptions = Object.keys(STUDENT_STATUS)
        .filter(d => d !== 'ONBOARDED' || studentDetails?.status === 'ONBOARDED')
        .map(d => ({
            value: d,
            label: d
        }))

    // Format date for input field
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            return format(new Date(dateString), 'yyyy-MM-dd');
        } catch {
            return '';
        }
    };

    return (
        <Box>
            <AppBar
                position={'fixed'}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
                elevation={0}
            >
                <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                    <Topbar onSidebarOpen={handleSidebarOpen} />
                </Container>
            </AppBar>
            <Sidebar
                onClose={handleSidebarClose}
                open={open}
                variant={isMd ? 'permanent' : 'temporary'}
            />
            <main>
                <Box height={{ xs: 94, sm: 102, md: 107 }} />
                <br />
                <br />

                <Box
                    overflow="hidden"
                    marginLeft={{ md: '270px' }}
                    paddingRight={{ xs: 2, md: 4 }}
                >
                    <Typography
                        variant={"h5"}
                        sx={{ marginBottom: 2 }}
                        fontWeight={700}
                    >
                        Edit Student
                    </Typography>

                    <Formik
                        initialValues={{
                            name: studentDetails?.name || '',
                            phoneNumber: studentDetails?.phoneNumber || '',
                            email: studentDetails?.email || '',
                            organizationId: studentDetails?.organizationId?._id ?? null,
                            status: studentDetails?.status || '',
                            address: studentDetails?.address || '',
                            pincode: studentDetails?.pincode || '',
                            city: studentDetails?.city || '',
                            state: studentDetails?.state || '',
                            guardianName: studentDetails?.guardianName || '',
                            guardianPhoneNumber: studentDetails?.guardianPhoneNumber || '',
                            dob: formatDateForInput(studentDetails?.dob),
                            learningLicenseNumber: studentDetails?.learningLicenseNumber || '',
                            learningLicenseImg: studentDetails?.learningLicenseImg || '',
                            addressProofImg: studentDetails?.addressProofImg || '',
                            bloodGroup: studentDetails?.bloodGroup || '',
                        }}

                        validationSchema={validationSchema}
                        onSubmit={(values) => handleOnSubmit(values)}
                        enableReinitialize
                    >
                        {(formikProps) => {
                            const { values, errors, touched, setFieldValue, handleSubmit, isSubmitting } =
                                formikProps;
                            return (
                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={{ xs: 2, md: 4 }} sx={{ padding: '10px' }}>
                                        {/* Basic Information */}
                                        <Grid item xs={12}>
                                            <Typography variant="h6" fontWeight={600} color="primary">
                                                Basic Information
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Name *
                                            </Typography>
                                            <TextField
                                                variant="outlined"
                                                name={"name"}
                                                fullWidth
                                                size="small"
                                                value={values.name}
                                                onChange={(e) =>
                                                    setFieldValue("name", e.target.value)
                                                }
                                                error={
                                                    touched.name && Boolean(errors.name)
                                                }
                                                helperText={touched.name && errors.name}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Roll No.
                                            </Typography>
                                            <TextField
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                value={studentDetails?.displayId || '-'}
                                                InputProps={{ readOnly: true }}
                                                helperText="System-generated. Cannot be edited."
                                                sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Phone Number *
                                            </Typography>
                                            <TextField
                                                variant="outlined"
                                                name={"phoneNumber"}
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={values.phoneNumber}
                                                onChange={(e) =>
                                                    e.target.value.toString().length > 10 ?
                                                        setFieldValue("phoneNumber", e.target.value.substring(0, 10))
                                                        :
                                                        setFieldValue("phoneNumber", e.target.value)
                                                }
                                                error={
                                                    touched.phoneNumber && Boolean(errors.phoneNumber)
                                                }
                                                helperText={touched.phoneNumber && errors.phoneNumber}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Email
                                            </Typography>
                                            <TextField
                                                variant="outlined"
                                                name={"email"}
                                                fullWidth
                                                size="small"
                                                type="email"
                                                value={values.email}
                                                onChange={(e) =>
                                                    setFieldValue("email", e.target.value)
                                                }
                                                error={
                                                    touched.email && Boolean(errors.email)
                                                }
                                                helperText={touched.email && errors.email}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Date of Birth
                                            </Typography>
                                            <TextField
                                                variant="outlined"
                                                name={"dob"}
                                                fullWidth
                                                size="small"
                                                type="date"
                                                value={values.dob}
                                                onChange={(e) =>
                                                    setFieldValue("dob", e.target.value)
                                                }
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Blood Group
                                            </Typography>
                                            <Select
                                                styles={{
                                                    menu: provided => ({ ...provided, zIndex: 9999 }),
                                                    control: provided => ({ ...provided, minHeight: 40 })
                                                }}
                                                placeholder="Select Blood Group"
                                                value={BLOOD_GROUP_OPTIONS.find(opt => opt.value === values.bloodGroup) || null}
                                                options={BLOOD_GROUP_OPTIONS}
                                                isClearable
                                                onChange={(e) => {
                                                    setFieldValue("bloodGroup", e?.value || '')
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Status
                                            </Typography>
                                            <Select
                                                styles={{
                                                    menu: provided => ({ ...provided, zIndex: 9999 }),
                                                    control: provided => ({ ...provided, minHeight: 40 })
                                                }}
                                                placeholder="Select Status"
                                                value={statusOptions.find(d => d.value === values.status) || null}
                                                options={statusOptions}
                                                onChange={(e) => {
                                                    setFieldValue("status", e.value)
                                                }}
                                            />
                                        </Grid>

                                        {profile === PROFILES.ADMIN &&
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Typography
                                                    variant={"subtitle2"}
                                                    sx={{ marginBottom: 1 }}
                                                    fontWeight={700}
                                                >
                                                    Organization
                                                </Typography>
                                                <Select
                                                    styles={{
                                                        menu: provided => ({ ...provided, zIndex: 9999 }),
                                                        control: provided => ({ ...provided, minHeight: 40 })
                                                    }}
                                                    isDisabled={studentDetails?.organizationId !== null && studentDetails?.organizationId !== undefined}
                                                    placeholder="Select Organization"
                                                    value={
                                                        studentDetails?.organizationId
                                                            ? { value: studentDetails.organizationId._id, label: studentDetails.organizationId.name }
                                                            : (values.organizationId ? organizations.find(d => d.value === values.organizationId) : null)
                                                    }
                                                    options={organizations}
                                                    onChange={(e) => {
                                                        setFieldValue("organizationId", e.value)
                                                    }}
                                                />
                                            </Grid>
                                        }

                                        {/* Profile Image */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Profile Image
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                {profileImgPreview && (
                                                    <Box sx={{ position: 'relative' }}>
                                                        <Box
                                                            component="img"
                                                            src={profileImgPreview}
                                                            onClick={() => handlePreviewClick(profileImgPreview)}
                                                            sx={{
                                                                width: 80,
                                                                height: 80,
                                                                objectFit: 'cover',
                                                                borderRadius: '50%',
                                                                border: '2px solid',
                                                                borderColor: 'divider',
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveImage('profile', setFieldValue)}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: -4,
                                                                right: -4,
                                                                bgcolor: 'error.main',
                                                                color: 'white',
                                                                '&:hover': { bgcolor: 'error.dark' },
                                                                width: 22,
                                                                height: 22,
                                                            }}
                                                        >
                                                            <CloseIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                                <Box>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        hidden
                                                        ref={profileImgInputRef}
                                                        onChange={(e) => handleImageSelect(e, 'profile')}
                                                    />
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<CloudUploadIcon />}
                                                        onClick={() => profileImgInputRef.current?.click()}
                                                        size="small"
                                                    >
                                                        {profileImgPreview ? 'Change' : 'Upload'}
                                                    </Button>
                                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                                        Max: 5MB
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* Guardian Information */}
                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                            <Typography variant="h6" fontWeight={600} color="primary">
                                                Guardian Information
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Guardian Name
                                            </Typography>
                                            <TextField
                                                placeholder="Enter Guardian Name"
                                                variant="outlined"
                                                name={"guardianName"}
                                                fullWidth
                                                size="small"
                                                value={values.guardianName}
                                                onChange={(e) =>
                                                    setFieldValue("guardianName", e.target.value)
                                                }
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Guardian Phone Number
                                            </Typography>
                                            <TextField
                                                placeholder="Enter Guardian Phone Number"
                                                variant="outlined"
                                                name={"guardianPhoneNumber"}
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={values.guardianPhoneNumber}
                                                onChange={(e) =>
                                                    e.target.value.toString().length > 10 ?
                                                        setFieldValue("guardianPhoneNumber", e.target.value.substring(0, 10))
                                                        :
                                                        setFieldValue("guardianPhoneNumber", e.target.value)
                                                }
                                                error={
                                                    touched.guardianPhoneNumber && Boolean(errors.guardianPhoneNumber)
                                                }
                                                helperText={touched.guardianPhoneNumber && errors.guardianPhoneNumber}
                                            />
                                        </Grid>

                                        {/* Address Information */}
                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                            <Typography variant="h6" fontWeight={600} color="primary">
                                                Address Information
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={8}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Address
                                            </Typography>
                                            <TextField
                                                placeholder="Enter Address"
                                                variant="outlined"
                                                name={"address"}
                                                fullWidth
                                                size="small"
                                                value={values.address}
                                                onChange={(e) =>
                                                    setFieldValue("address", e.target.value)
                                                }
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                State
                                            </Typography>
                                            <Select
                                                styles={{
                                                    menu: provided => ({ ...provided, zIndex: 9999 }),
                                                    control: provided => ({ ...provided, minHeight: 40 })
                                                }}
                                                placeholder="Select State"
                                                value={selectedState}
                                                options={stateOptions}
                                                isClearable
                                                onChange={(e) => {
                                                    setSelectedState(e);
                                                    setSelectedCity(null);
                                                    setFieldValue("state", e?.value || '');
                                                    setFieldValue("city", '');
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                City
                                            </Typography>
                                            <Select
                                                styles={{
                                                    menu: provided => ({ ...provided, zIndex: 9999 }),
                                                    control: provided => ({ ...provided, minHeight: 40 })
                                                }}
                                                placeholder="Select City"
                                                value={selectedCity}
                                                options={cityOptions}
                                                isClearable
                                                isDisabled={!selectedState}
                                                onChange={(e) => {
                                                    setSelectedCity(e);
                                                    setFieldValue("city", e?.value || '');
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Pincode
                                            </Typography>
                                            <TextField
                                                placeholder="Enter Pincode"
                                                variant="outlined"
                                                name={"pincode"}
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={values.pincode}
                                                onChange={(e) => {
                                                    const val = e.target.value.toString();
                                                    if (val.length <= 6) {
                                                        setFieldValue("pincode", val);
                                                    }
                                                }}
                                                inputProps={{ maxLength: 6 }}
                                            />
                                        </Grid>

                                        {/* License & Documents */}
                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                            <Typography variant="h6" fontWeight={600} color="primary">
                                                License & Documents
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Learning License Number
                                            </Typography>
                                            <TextField
                                                placeholder="Enter Learning License Number"
                                                variant="outlined"
                                                name={"learningLicenseNumber"}
                                                fullWidth
                                                size="small"
                                                value={values.learningLicenseNumber}
                                                onChange={(e) =>
                                                    setFieldValue("learningLicenseNumber", e.target.value)
                                                }
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Learning License Image
                                            </Typography>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                ref={learningLicenseInputRef}
                                                onChange={(e) => handleImageSelect(e, 'learningLicense')}
                                                style={{ display: 'none' }}
                                            />
                                            {learningLicenseImgPreview ? (
                                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                                    <Box
                                                        component="img"
                                                        src={learningLicenseImgPreview}
                                                        alt="Learning License"
                                                        onClick={() => handlePreviewClick(learningLicenseImgPreview)}
                                                        sx={{
                                                            width: 120,
                                                            height: 90,
                                                            objectFit: 'cover',
                                                            borderRadius: 1,
                                                            cursor: 'pointer',
                                                            border: `1px solid ${theme.palette.divider}`,
                                                        }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemoveImage('learningLicense', setFieldValue)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -8,
                                                            right: -8,
                                                            bgcolor: 'error.main',
                                                            color: 'white',
                                                            '&:hover': { bgcolor: 'error.dark' },
                                                            width: 24,
                                                            height: 24,
                                                        }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<CloudUploadIcon />}
                                                    onClick={() => learningLicenseInputRef.current?.click()}
                                                    sx={{ height: 40 }}
                                                >
                                                    Upload Image
                                                </Button>
                                            )}
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Address Proof Image
                                            </Typography>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                ref={addressProofInputRef}
                                                onChange={(e) => handleImageSelect(e, 'addressProof')}
                                                style={{ display: 'none' }}
                                            />
                                            {addressProofImgPreview ? (
                                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                                    <Box
                                                        component="img"
                                                        src={addressProofImgPreview}
                                                        alt="Address Proof"
                                                        onClick={() => handlePreviewClick(addressProofImgPreview)}
                                                        sx={{
                                                            width: 120,
                                                            height: 90,
                                                            objectFit: 'cover',
                                                            borderRadius: 1,
                                                            cursor: 'pointer',
                                                            border: `1px solid ${theme.palette.divider}`,
                                                        }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemoveImage('addressProof', setFieldValue)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -8,
                                                            right: -8,
                                                            bgcolor: 'error.main',
                                                            color: 'white',
                                                            '&:hover': { bgcolor: 'error.dark' },
                                                            width: 24,
                                                            height: 24,
                                                        }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<CloudUploadIcon />}
                                                    onClick={() => addressProofInputRef.current?.click()}
                                                    sx={{ height: 40 }}
                                                >
                                                    Upload Image
                                                </Button>
                                            )}
                                        </Grid>

                                        {/* Additional Documents */}
                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                            <Typography variant="h6" fontWeight={600} color="primary">
                                                Additional Documents
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={4} md={3}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Document Name
                                            </Typography>
                                            <TextField
                                                placeholder="e.g., 10th Memo"
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                value={newDocName}
                                                onChange={(e) => setNewDocName(e.target.value)}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={4} md={3}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Document Image
                                            </Typography>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                ref={documentInputRef}
                                                onChange={(e) => handleImageSelect(e, 'document')}
                                                style={{ display: 'none' }}
                                            />
                                            {newDocPreview ? (
                                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                                    <Box
                                                        component="img"
                                                        src={newDocPreview}
                                                        alt="Document preview"
                                                        sx={{
                                                            width: 80,
                                                            height: 60,
                                                            objectFit: 'cover',
                                                            borderRadius: 1,
                                                            border: `1px solid ${theme.palette.divider}`,
                                                        }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemoveImage('document')}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -8,
                                                            right: -8,
                                                            bgcolor: 'error.main',
                                                            color: 'white',
                                                            '&:hover': { bgcolor: 'error.dark' },
                                                            width: 20,
                                                            height: 20,
                                                        }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<CloudUploadIcon />}
                                                    onClick={() => documentInputRef.current?.click()}
                                                    sx={{ height: 40 }}
                                                >
                                                    Select
                                                </Button>
                                            )}
                                        </Grid>

                                        <Grid item xs={12} sm={2} md={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<AddIcon />}
                                                onClick={handleAddDocument}
                                                size="medium"
                                                disabled={!newDocName.trim() || !newDocFile}
                                            >
                                                Add
                                            </Button>
                                        </Grid>

                                        {documents.length > 0 && (
                                            <Grid item xs={12}>
                                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                    {documents.map((doc, index) => (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                position: 'relative',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                p: 1,
                                                                bgcolor: 'grey.100',
                                                                borderRadius: 1,
                                                                width: 120,
                                                            }}
                                                        >
                                                            <Box
                                                                component="img"
                                                                src={doc.url}
                                                                alt={doc.name}
                                                                onClick={() => handlePreviewClick(doc.url)}
                                                                sx={{
                                                                    width: 100,
                                                                    height: 75,
                                                                    objectFit: 'cover',
                                                                    borderRadius: 1,
                                                                    cursor: 'pointer',
                                                                    border: `1px solid ${theme.palette.divider}`,
                                                                }}
                                                            />
                                                            <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 600, textAlign: 'center' }}>
                                                                {doc.name}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRemoveDocument(index)}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: -8,
                                                                    right: -8,
                                                                    bgcolor: 'error.main',
                                                                    color: 'white',
                                                                    '&:hover': { bgcolor: 'error.dark' },
                                                                    width: 24,
                                                                    height: 24,
                                                                }}
                                                            >
                                                                <CloseIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Grid>
                                        )}

                                    </Grid>
                                    <br />
                                    <Button
                                        component={'button'}
                                        variant={"contained"}
                                        fontWeight={700}
                                        type='submit'
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? <CircularProgress size={24} /> : "Update"}
                                    </Button>
                                    <br />
                                    <br />

                                </form>
                            )
                        }}
                    </Formik>
                </Box>
            </main>

            {/* Image Preview Dialog */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    <IconButton
                        onClick={() => setPreviewOpen(false)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box
                        component="img"
                        src={previewImage}
                        alt="Preview"
                        sx={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '80vh',
                            objectFit: 'contain',
                        }}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default EditStudent;
